import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle, faTrashAlt} from "@fortawesome/free-solid-svg-icons";
import QTimePicker from "../../../elements/QTimePicker";
import FloatingMenuPortal from "../../../elements/FloatingMenuPortal";

const fixedPeriodDays = {DAILY: 1, WEEKEND: 2, WEEKLY: 7, BIWEEKLY: 14, MONTHLY: 31};
const weekDays = ["Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota", "Niedziela"];

const getMaxDays = (plan) => plan.periodType === "CUSTOM"
  ? Math.max(1, Number(plan.periodDays || 1))
  : (fixedPeriodDays[plan.periodType] || 1);

const dayLabel = (periodType, dayIndex) => {
  if (periodType === "DAILY") return "Dzień";
  if (periodType === "WEEKEND") return dayIndex === 1 ? "Sobota" : "Niedziela";
  if (periodType === "WEEKLY") return weekDays[dayIndex - 1] || `Dzień ${dayIndex}`;
  if (periodType === "BIWEEKLY") {
    const week = dayIndex <= 7 ? 1 : 2;
    return `T${week} ${weekDays[(dayIndex - 1) % 7]}`;
  }
  return `Dzień ${dayIndex}`;
};

const ResourcePlanItem = ({
  resourcePlanItem,
  index,
  state,
  onInputChange,
  positionsList,
  departmentsList,
  onRemove
}) => {
  const formatDateToTimeString = (date) => {
    if (!date) return null;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const parseTimeToDate = (timeString) => {
    if (!timeString) return null;
    if (typeof timeString === 'string') {
      const [hours, minutes] = timeString.split(':').map(Number);
      return new Date(1970, 0, 1, hours || 0, minutes || 0, 0);
    }
    if (timeString instanceof Date) return timeString;
    return null;
  };

  const [workStart, setWorkStart] = useState(
    resourcePlanItem.workStart ? parseTimeToDate(resourcePlanItem.workStart)
      : state.currentItem.workStart ? parseTimeToDate(state.currentItem.workStart) : null
  );
  const [workEnd, setWorkEnd] = useState(
    resourcePlanItem.workEnd ? parseTimeToDate(resourcePlanItem.workEnd)
      : state.currentItem.workEnd ? parseTimeToDate(state.currentItem.workEnd) : null
  );
  const [positionMenuOpen, setPositionMenuOpen] = useState(false);
  const [positionSearch, setPositionSearch] = useState("");
  const positionMenuRef = useRef(null);
  const positionTriggerRef = useRef(null);

  const updateRow = (changes) => {
    state.setCurrentItem((prev) => {
      const updatedItems = [...(prev.resourcePlanItems || [])];
      const targetIndex = updatedItems.findIndex((item) => item.id === resourcePlanItem.id);
      const resolvedIndex = targetIndex >= 0 ? targetIndex : index;
      if (!updatedItems[resolvedIndex]) return prev;
      updatedItems[resolvedIndex] = {...updatedItems[resolvedIndex], ...changes};
      return {...prev, resourcePlanItems: updatedItems};
    });
    state.setItemChanged((prev) => !prev);
    state.setCurrentFormState((prev) => ({...prev, formSaveButtonDisabled: false}));
  };

  useEffect(() => {
    const changes = {};
    if (!resourcePlanItem.workStart && state.currentItem.workStart) {
      changes.workStart = formatDateToTimeString(parseTimeToDate(state.currentItem.workStart));
    }
    if (!resourcePlanItem.workEnd && state.currentItem.workEnd) {
      changes.workEnd = formatDateToTimeString(parseTimeToDate(state.currentItem.workEnd));
    }
    if (!resourcePlanItem.dayIndex) changes.dayIndex = 1;
    if (!Array.isArray(resourcePlanItem.positions)) {
      changes.positions = (!resourcePlanItem.allOtherPositions && Number(resourcePlanItem.position?.id || 0) > 0)
        ? [resourcePlanItem.position]
        : [];
    }
    if (resourcePlanItem.allOtherPositions !== false) changes.allOtherPositions = false;
    if (resourcePlanItem.position) changes.position = null;
    if (Object.keys(changes).length > 0) updateRow(changes);
  }, []);

  useEffect(() => {
    setWorkStart(parseTimeToDate(resourcePlanItem.workStart || state.currentItem.workStart));
    setWorkEnd(parseTimeToDate(resourcePlanItem.workEnd || state.currentItem.workEnd));
  }, [resourcePlanItem.workStart, resourcePlanItem.workEnd, state.currentItem.workStart, state.currentItem.workEnd]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      const insideTrigger = positionTriggerRef.current && positionTriggerRef.current.contains(event.target);
      const insideMenu = positionMenuRef.current && positionMenuRef.current.contains(event.target);
      if (!insideTrigger && !insideMenu) setPositionMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);


  const departmentId = Number(resourcePlanItem.department?.id || 0);
  const maxDays = getMaxDays(state.currentItem);

  const positionsForDepartment = useMemo(() => (
    (positionsList || [])
      .filter((position) => Number(position.department?.id || 0) === departmentId)
      .filter((position) => position.active !== false)
      .filter((position) => String(position.name || "").toLocaleLowerCase('pl').includes(positionSearch.toLocaleLowerCase('pl')))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pl'))
  ), [positionsList, departmentId, positionSearch]);

  const selectedPositions = useMemo(() => {
    const positions = Array.isArray(resourcePlanItem.positions) ? resourcePlanItem.positions : [];
    if (positions.length > 0) return positions;
    if (!resourcePlanItem.allOtherPositions && Number(resourcePlanItem.position?.id || 0) > 0) {
      return [resourcePlanItem.position];
    }
    return [];
  }, [resourcePlanItem.positions, resourcePlanItem.position, resourcePlanItem.allOtherPositions]);

  const selectedPositionIds = useMemo(
    () => new Set(selectedPositions.map((position) => Number(position.id)).filter((id) => id > 0)),
    [selectedPositions]
  );

  const handleDepartmentChange = (e) => {
    const id = Number(e.target.value || 0);
    const department = (departmentsList || []).find((item) => Number(item.id) === id) || {id: 0};
    updateRow({department, positions: [], position: null, allOtherPositions: false});
    setPositionMenuOpen(false);
    setPositionSearch("");
  };

  const togglePosition = (position) => {
    const id = Number(position.id);
    const next = selectedPositionIds.has(id)
      ? selectedPositions.filter((item) => Number(item.id) !== id)
      : [...selectedPositions, position];
    updateRow({positions: next, position: null, allOtherPositions: false});
  };

  const clearPositions = () => {
    updateRow({positions: [], position: null, allOtherPositions: false});
  };

  const positionSelectionLabel = () => {
    if (selectedPositions.length === 0) return "Dowolne stanowisko w dziale";
    if (selectedPositions.length === 1) return selectedPositions[0].name || "1 stanowisko";
    if (selectedPositions.length <= 2) return selectedPositions.map((position) => position.name).join(", ");
    return `${selectedPositions.length} wybrane stanowiska`;
  };

  return (
    <div className="resource-plan-items-grid resource-plan-items-row">
      <div>
        <select className="form-select" value={Number(resourcePlanItem.dayIndex || 1)} onChange={(e) => updateRow({dayIndex: Number(e.target.value)})}>
          {Array.from({length: maxDays}, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>{dayLabel(state.currentItem.periodType || "DAILY", day)}</option>
          ))}
        </select>
      </div>

      <div>
        <select className="form-select" value={departmentId || ""} onChange={handleDepartmentChange}>
          <option value="">-- wybierz dział --</option>
          {(departmentsList || []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
        </select>
      </div>

      <div className="resource-plan-position-cell">
        <button
          ref={positionTriggerRef}
          type="button"
          className="form-select resource-plan-position-trigger"
          disabled={!departmentId}
          onClick={() => setPositionMenuOpen((open) => !open)}
          title={positionSelectionLabel()}
        >
          {positionSelectionLabel()}
        </button>
        <FloatingMenuPortal
          open={positionMenuOpen && departmentId > 0}
          anchorRef={positionTriggerRef}
          menuRef={positionMenuRef}
          minWidth={360}
          preferredWidth={520}
          maxHeight={430}
          className="resource-plan-position-menu resource-plan-position-menu-portal"
        >
          {(layout) => (
            <>
              <div className="small text-muted mb-2">
                Brak zaznaczenia oznacza dowolne aktywne stanowisko w tym dziale. Możesz zaznaczyć kilka stanowisk jako wspólną pulę.
              </div>
              <input
                type="text"
                className="form-control form-control-sm mb-2"
                value={positionSearch}
                onChange={(e) => setPositionSearch(e.target.value)}
                placeholder="Wyszukaj stanowisko..."
                autoFocus
              />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearPositions}>Wyczyść wybór</button>
                <span className="small text-muted">Wybrano: {selectedPositions.length}</span>
              </div>
              <div
                className="resource-plan-position-menu-list"
                style={{maxHeight: `${Math.max(100, layout.maxHeight - 135)}px`}}
              >
                {positionsForDepartment.length === 0 ? (
                  <div className="small text-danger py-2">Brak pasujących aktywnych stanowisk w wybranym dziale.</div>
                ) : positionsForDepartment.map((position) => (
                  <label key={position.id} className="resource-plan-position-option">
                    <input
                      type="checkbox"
                      checked={selectedPositionIds.has(Number(position.id))}
                      onChange={() => togglePosition(position)}
                    />
                    <span>{position.name}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </FloatingMenuPortal>
      </div>

      <div>
        <input
          type="number"
          id={`resourcePlanItem_requiredStaff-${resourcePlanItem.id}`}
          value={resourcePlanItem.requiredPeopleCount || ""}
          onChange={(e) => onInputChange(e, resourcePlanItem, "requiredPeopleCount")}
          min={1}
          max={999}
          placeholder="Osoby"
          className="form-control"
        />
      </div>

      <div>
        <input
          type="number"
          id={`resourcePlanItem_priority-${resourcePlanItem.id}`}
          value={resourcePlanItem.priority ?? ""}
          onChange={(e) => onInputChange(e, resourcePlanItem, "priority")}
          min={0}
          max={99}
          placeholder="Priorytet"
          className="form-control"
        />
      </div>

      <div>
        <QTimePicker
          pickerId={`workStart-${resourcePlanItem.id}`}
          errorParagraphId={`wrongWorkStartTimeFormat-rpi${resourcePlanItem.id}`}
          state={state}
          propertyName={`resourcePlanItems[${index}].workStart`}
          startTime={workStart}
          setStartTime={(time) => {
            setWorkStart(time);
            updateRow({workStart: time ? formatDateToTimeString(time) : null});
          }}
          caption="Godzina rozpoczęcia pracy"
          commitToState={false}
        />
        <p id={`wrongWorkStartTimeFormat-rpi${resourcePlanItem.id}`} className="err_info offscreen">
          <FontAwesomeIcon icon={faInfoCircle}/>&nbsp; Nieprawidłowy format czasu
        </p>
      </div>

      <div>
        <QTimePicker
          pickerId={`workEnd-${resourcePlanItem.id}`}
          errorParagraphId={`wrongWorkEndTimeFormat-rpi${resourcePlanItem.id}`}
          state={state}
          propertyName={`resourcePlanItems[${index}].workEnd`}
          startTime={workEnd}
          setStartTime={(time) => {
            setWorkEnd(time);
            updateRow({workEnd: time ? formatDateToTimeString(time) : null});
          }}
          caption="Godzina zakończenia pracy"
          commitToState={false}
        />
        <p id={`wrongWorkEndTimeFormat-rpi${resourcePlanItem.id}`} className="err_info offscreen">
          <FontAwesomeIcon icon={faInfoCircle}/>&nbsp; Nieprawidłowy format czasu
        </p>
      </div>

      <div className="resource-plan-remove-cell">
        <button type="button" className="btn btn-outline-danger" onClick={onRemove} title="Usuń pozycję">
          <FontAwesomeIcon icon={faTrashAlt}/>
        </button>
      </div>
    </div>
  );
};

export default ResourcePlanItem;
